import type { AuthMeResponse } from "../types/api";

/** Owns identity transitions and ignores stale async work, including A→B→A races. */
export function createProfileLifecycle(options: {
  fetchProfile: () => Promise<AuthMeResponse>;
  onProfile: (profile: AuthMeResponse) => void;
  onReset: () => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  let identity: string | null = null;
  let revision = 0;
  let active = true;

  return {
    setIdentity(next: string | null) {
      if (!active) return false;
      const changed = identity !== next;
      if (changed) {
        identity = next;
        revision += 1;
        options.onReset();
        options.onLoadingChange(Boolean(next));
      } else if (!next) {
        options.onLoadingChange(false);
      }
      return changed;
    },
    isIdentity(expected: string) {
      return active && identity === expected;
    },
    async load() {
      if (!active || !identity) return false;
      const expectedIdentity = identity;
      const request = ++revision;
      const isCurrent = () =>
        active && identity === expectedIdentity && revision === request;
      options.onLoadingChange(true);
      try {
        const profile = await options.fetchProfile();
        if (!isCurrent()) return false;
        if (profile.access.authUserId !== expectedIdentity) {
          throw new Error(
            "Your access profile does not match the signed-in account. Try loading it again.",
          );
        }
        options.onProfile(profile);
        return true;
      } catch (error) {
        if (isCurrent()) throw error;
        return false;
      } finally {
        if (isCurrent()) options.onLoadingChange(false);
      }
    },
    dispose() {
      active = false;
      revision += 1;
    },
  };
}
